import { TextV2 } from 'components/TextV2/TextV2';
import { useBuildYourOwn } from 'features/build-your-own/hooks/useBuildYourOwn';
import React, { useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import styles from './SystemPreviewDrawer.module.scss';
import { BYOStepId } from 'api/contentful/contentTypes.cloverdotcom';
import { SelectionItem } from './SelectionItem';
import { changePhoneNumberDynamicallyAfterTimeout } from 'utils/InvocaUtils';
import { useSelectedItems } from 'features/build-your-own/hooks/sub-hooks/useSelectedItems';

const transitionClassNames = {
  enterActive: styles.previewDrawerEnterActive,
  enterDone: styles.previewDrawerEnterDone,
  exit: styles.previewDrawerExit,
};

interface ISystemPreviewDrawerProps {
  showDrawer: boolean;
  drawerRef: React.RefObject<HTMLDivElement>;
}

export const SystemPreviewDrawer: React.FC<ISystemPreviewDrawerProps> = ({ showDrawer, drawerRef }) => {
  const { currentStepData, systemReviewEmptyText, systemReviewTitle, selectionState, pricingDisclaimerText } =
    useBuildYourOwn();
  const selectedItems = useSelectedItems(selectionState);
  const drawerFocus = useRef<HTMLSpanElement>(null);
  const selectedPlan = Object.values(selectionState[BYOStepId.POS_NEEDS])[0];
  const selectedDevices = Object.values(selectionState[BYOStepId.DEVICES]);
  const selectedAccessories = Object.values(selectionState[BYOStepId.ACCESSORIES]);
  const hasDevicesOrAccessories = !!selectedDevices.length || !!selectedAccessories.length;
  const isEmpty = !selectedPlan && !hasDevicesOrAccessories;
  const shouldShowDisclaimer = hasDevicesOrAccessories;

  useEffect(() => {
    if (shouldShowDisclaimer) changePhoneNumberDynamicallyAfterTimeout();
  }, [shouldShowDisclaimer]);

  useEffect(() => {
    if (showDrawer && drawerFocus.current) {
      drawerFocus.current.focus();
    }
  }, [showDrawer, drawerFocus]);

  if (!currentStepData) return null;

  const showDisclaimerText = shouldShowDisclaimer ? (
    <TextV2 value={pricingDisclaimerText} variant="body-small" className={styles.disclaimer} isMarkdown />
  ) : null;

  return (
    <CSSTransition
      in={showDrawer}
      timeout={300}
      className={styles.previewDrawer}
      classNames={transitionClassNames}
    >
      <div ref={drawerRef}>
        <TextV2 value={systemReviewTitle} variant="h4-bold" tabIndex={0} ref={drawerFocus} />
        <div className={styles.content}>
          {isEmpty ? (
            <TextV2 value={systemReviewEmptyText} variant="h5" />
          ) : (
            <>
              {selectedItems.map(({ secondaryTitle, itemKey, dcsIds, quantity, contentType }) => (
                <SelectionItem
                  label={secondaryTitle}
                  key={itemKey}
                  dcsIds={dcsIds}
                  quantity={quantity}
                  contentType={contentType}
                  isSystemPreviewDrawer
                />
              ))}
              {showDisclaimerText}
            </>
          )}
        </div>
      </div>
    </CSSTransition>
  );
};
